import { captureException, captureMessage, addBreadcrumb, setUserContext } from "./sentry";
import { handleApiError, type ApiError } from "@/lib/api/errors";
import type { ApiResponse } from "@/lib/api/types";

export function reportError(error: unknown, context?: Record<string, unknown>): void {
  const apiError = error instanceof Error ? error : new Error(String(error));
  captureException(apiError, context);
  addBreadcrumb({
    category: "error",
    message: apiError.message,
    level: "error",
    data: context,
  });
}

export function reportApiError(error: unknown, url: string, method: string, body?: unknown): void {
  const apiError = handleApiError(error);
  captureException(apiError, {
    url,
    method,
    body,
    status: apiError.status,
    code: apiError.code,
  });
  addBreadcrumb({
    category: "api",
    message: `${method} ${url} — ${apiError.status}`,
    level: "error",
    data: { url, method, status: apiError.status, code: apiError.code },
  });
}

export function reportApiResponse<T>(response: ApiResponse<T> | T, url: string, method: string): void {
  if (response && typeof response === "object" && "success" in response) {
    const apiResponse = response as ApiResponse<T>;
    if (!apiResponse.success) {
      captureMessage(`API error response: ${method} ${url} — ${apiResponse.message}`, "warning");
      addBreadcrumb({
        category: "api",
        message: `${method} ${url} — error response`,
        level: "warning",
        data: { url, method, message: apiResponse.message },
      });
    }
  }
}

export function reportUserAction(action: string, details?: Record<string, unknown>): void {
  addBreadcrumb({
    category: "user_action",
    message: action,
    level: "info",
    data: details,
  });
}

export function reportRouteChange(pathname: string, prevPathname: string | null): void {
  addBreadcrumb({
    category: "navigation",
    message: `Route change: ${prevPathname ?? "initial"} → ${pathname}`,
    level: "info",
    data: { from: prevPathname, to: pathname },
  });
}

export function reportPerformanceMetric(name: string, value: number, rating: "good" | "needs-improvement" | "poor"): void {
  addBreadcrumb({
    category: "performance",
    message: `${name}: ${value}ms (${rating})`,
    level: rating === "poor" ? "warning" : "info",
    data: { name, value, rating },
  });
}

export function initErrorReporting(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    reportError(event.error, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      type: "global_error",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, {
      type: "unhandled_promise_rejection",
    });
  });
}

export function setUserForReporting(user: { id?: string; email?: string; role?: string } | null): void {
  setUserContext(user);
}